import createCachedSelector from "re-reselect";
import { type LineItem } from "../../models/CartData";
import * as PriceUtils from "../../../../../utils/PriceUtils";
import { zeroNetPrice } from "../../../../../utils/PriceUtils";
import { type RootState } from "~/app/store";
import { type NetPriceData, type PriceData } from "~/domains/pricing/types/PriceData";
import {
  getSurchargeValueFromTotalPrice,
  getTotalPriceForLineItem,
} from "~/domains/product/utils/productDescriptorUtils";
import { type ProductData } from "~/domains/product/types/ProductData";
import { selectLineItemsByProduct } from "~/features/cart/cart/redux/selectors/cartSelectors";
import { selectProductData } from "~/domains/product/redux/selectors";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

const selectPackageEntriesForProductGroup = createCachedSelector<
  RootState,
  { productGroupNumber: number },
  Dict<LineItem>,
  number,
  LineItem[]
>(
  (state: RootState) => state.cart.packageEntriesById as Dict<LineItem>,
  (_, { productGroupNumber }) => productGroupNumber,
  (packageEntriesById, productGroupNumber) =>
    Object.values(packageEntriesById).filter((e) => e.groupNumbers.includes(productGroupNumber)),
)((_, props) => props.productGroupNumber);

// TODO: These should be only top level line items, not sub line items?
export const selectLineItemsForProduct = createCachedSelector<
  RootState,
  { productCode: string },
  Record<string, LineItem[]>,
  string,
  LineItem[]
>(
  selectLineItemsByProduct,
  (_, { productCode }) => productCode,
  (lineItemByProduct, productCode) => lineItemByProduct[productCode] ?? [],
)((_, props) => props.productCode);

export const selectTotalQty = createCachedSelector<RootState, { productCode: string }, LineItem[], number>(
  selectLineItemsForProduct,
  (lineItems) => lineItems.reduce((sum, entry) => sum + entry.quantity, 0),
)((_, props) => props.productCode);

export const selectTotalSurcharge = createCachedSelector<
  RootState,
  { productGroupNumber: number; quantityVisible: boolean },
  LineItem[],
  boolean,
  PriceData
>(
  selectPackageEntriesForProductGroup,
  (_, { quantityVisible }) => quantityVisible,
  (entries, quantityVisible) => {
    const surcharges = entries.filter(lineItemHasSurcharge).map((entry) => {
      const totalPrice = getTotalPriceForLineItem(entry, !quantityVisible);
      return getSurchargeValueFromTotalPrice(totalPrice);
    });

    return PriceUtils.volatileSum(...surcharges);
  },
)((_, props) => props.productGroupNumber);

export const selectCartEntryHasSurcharge = createCachedSelector<
  RootState,
  { productGroupNumber: number },
  LineItem[],
  boolean
>(selectPackageEntriesForProductGroup, (entries) => entries.some(lineItemHasSurcharge))(
  (_, props) => props.productGroupNumber,
);

/**
 * If the price was overriden by the salesman, returns offerPrice. Otherwise returns netPrice
 * @param packEntry the line item
 * @returns offer price or net price
 */
export const getVisibleNetPrice = (
  packEntry: LineItem,
  productOrSubscription: ProductData | ProductDescriptor | boolean,
): NetPriceData | undefined => {
  if (!packEntry.priceOverriden) {
    return packEntry.netPrice;
  }
  if (!packEntry.offerPrice) {
    return undefined;
  }
  return {
    ...(packEntry.netPrice ?? zeroNetPrice(productOrSubscription)),
    value: packEntry.offerPrice.value,
    baseValue: packEntry.offerPrice.value,
    surcharge: 0,
  };
};

export const selectSubtotal = (state: RootState, props: { productCode: string; isInCheckout: boolean }) => {
  const entries = selectLineItemsForProduct(state, { productCode: props.productCode });
  const productData = selectProductData(props.productCode)(state);
  const prices = entries.map((entry) => {
    return getTotalPriceForLineItem(entry, !productData.quantityVisible);
  });
  return PriceUtils.volatileSum(...prices);
};

export const selectCartContainsOptime = (state: RootState) =>
  Object.keys(selectLineItemsByProduct(state)).some(
    (productCode) => state.products.basic[productCode]?.isOptime ?? false,
  );

export const selectCartContainsDigitalServiceTenant = (state: RootState) =>
  Object.keys(selectLineItemsByProduct(state)).some(
    (productCode) => !(state.products.basic[productCode]?.quantityVisible ?? false),
  );

export const lineItemHasSurcharge = (lineItem: LineItem) => (lineItem.netPrice?.surcharge ?? 0) > 0;
export const lineItemHasTariffSurcharge = (lineItem: LineItem) => !!lineItem.netPrice?.tariffSurcharge;
